import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { 
  Box, 
  Drawer, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Divider,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Sensors as SensorsIcon,
  LocationOn as LocationIcon,
  NotificationsActive as AlertsIcon,
  Event as EventsIcon,
  People as UsersIcon,
  Business as OrganizationsIcon,
  Search as SearchIcon,
  AccountCircle as AccountIcon,
  ExitToApp as LogoutIcon,
} from '@mui/icons-material';
import MDBox from '../components/shared/atoms/MDBox';
import MDButton from '../components/shared/atoms/MDButton';
import MDTypography from '../components/shared/atoms/MDTypography';
import { useAuthenticationContext } from '../../application/context/AuthenticationContext';
import { useNavigation } from '../../application/hooks/useNavigation';
import { NavigationService } from '../../application/services/NavigationService';
import { TEXTS } from '../utils/texts';
import { getBrandName } from '../utils/brandUtils';
import Logo from '../components/shared/atoms/Logo';
import Footer from '../components/shared/organisms/Footer';

interface AuthLayoutProps {
  children: React.ReactNode;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const appName = getBrandName();
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  const { user, isAuthenticated, isLoading, signOut } = useAuthenticationContext();
  const { navigate, getFilteredNavItems, isCurrentRoute } = useNavigation();

  const navItems = NavigationService.getDefaultNavItems({
    DashboardIcon,
    SensorsIcon,
    LocationIcon,
    AlertsIcon,
    EventsIcon,
    UsersIcon,
    OrganizationsIcon,
    SearchIcon,
  });

  const filteredNavItems = getFilteredNavItems(navItems, user);

  if (isLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh' 
        }}
      >
        <Typography>{TEXTS.common.loading}</Typography>
      </Box>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/sign-in" replace />;
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      handleProfileMenuClose();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const handleNavItemClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawerWidth = 280;

  const renderNavigationItems = () => (
    <List>
      {filteredNavItems.map((item) => 
        <ListItem key={item.key} disablePadding>
          <ListItemButton
            selected={isCurrentRoute(item.path)}
            onClick={() => handleNavItemClick(item.path)}
            sx={{
              mx: 2,
              my: 0.5,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'white',
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
              '&.Mui-selected:hover': {
                backgroundColor: 'primary.main',
                color: 'white',
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
            }}
          >
            <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.name}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            />
          </ListItemButton>
        </ListItem>
      )}
    </List>
  );

  const renderUserSection = () => (
    <MDBox 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      flexDirection="column"
      sx={{ p: 2 }}
    >
      <MDTypography variant="body2" color="text" textAlign="center" mb={1}>
        {user.email}
      </MDTypography>
      <MDButton 
        variant="outlined" 
        color="primary" 
        size="small"
        onClick={handleSignOut}
        sx={{ width: '100%' }}
      >
        <LogoutIcon sx={{ mr: 1, fontSize: '1rem' }} />
        {TEXTS.auth.signOut}
      </MDButton>
    </MDBox>
  );

  const mobileDrawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pt: '56px' }}>      
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {renderNavigationItems()}
      </Box>
      
      <Divider />
      
      {renderUserSection()}
      
      <Footer variant="sidebar" />
    </Box>
  );

  const desktopDrawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <MDTypography variant="h5" color="primary" fontWeight="bold">
          {appName}
        </MDTypography>
      </Box>
      
      <Divider />
      
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {renderNavigationItems()}
      </Box>
      
      <Divider />
      
      {renderUserSection()}
      
      <Footer variant="sidebar" />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          backgroundColor: 'background.paper',
          color: 'text.primary',
          // boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <IconButton
            color="inherit"
            aria-label={TEXTS.layout.openDrawer}
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { md: 'none' },
              p: { xs: 1, sm: 1.5 }
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flexGrow: 1,
            justifyContent: { xs: 'center', md: 'flex-start' }
          }}>
            <Logo 
              size={isMobile ? 28 : 32} 
              variant="icon" 
            />
            <Typography 
              variant="h6" 
              noWrap 
              component="div" 
              sx={{ 
                ml: 1.5,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                fontWeight: 600,
              }}
            >
              {appName}
            </Typography>
          </Box>
          
          
          <IconButton
            size={isMobile ? "medium" : "large"}
            edge="end"
            aria-label={TEXTS.layout.accountOfCurrentUser}
            aria-controls={TEXTS.layout.profileMenu}
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
            sx={{ p: { xs: 1, sm: 1.5 } }}
          >
            <Avatar sx={{ 
              width: { xs: 28, sm: 32 }, 
              height: { xs: 28, sm: 32 }, 
              bgcolor: 'primary.main' 
            }}>
              <AccountIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Menu
        id={TEXTS.layout.profileMenu}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {[
          <MenuItem key="profile" onClick={() => { navigate('/account'); handleProfileMenuClose(); }}>
            <AccountIcon sx={{ mr: 1 }} />
            {TEXTS.account.profileMenuItem}
          </MenuItem>,
          <MenuItem key="signout" onClick={handleSignOut}>
            <LogoutIcon sx={{ mr: 1 }} />
            {TEXTS.auth.signOut}
          </MenuItem>
        ]}
      </Menu>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: 'background.paper',
              zIndex: (theme) => theme.zIndex.drawer,
            },
          }}
        >
          {mobileDrawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: 'background.paper',
              borderRight: '1px solid',
              borderColor: 'divider',
              zIndex: (theme) => theme.zIndex.drawer,
            },
          }}
          open
        >
          {desktopDrawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 0, sm: 1, lg: 2 },
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 56 } }} />
        {children}
      </Box>
    </Box>
  );
};

export default AuthLayout;